import { useMutation } from "@tanstack/react-query";
import { fetchApiPost } from "../utils";

// ---------------------------------------------------------------------------
// Types — mirror @lhc/assistant (lhc-mono-bun) result envelope.
// Kept as a flat re-declaration here so the admin portal does not depend on
// the bun package directly. Update when that contract changes.
// ---------------------------------------------------------------------------

export type CommodityId = "C" | "S" | "MW" | "CNL";

export type PriceComponent = "open" | "high" | "low" | "close";
export type ComputedOutput = "price_signal";
export type StudyName = "dmi" | "rsi";

export type DataSeries =
  | { kind: "price_component"; value: PriceComponent }
  | { kind: "computed_output"; value: ComputedOutput }
  | { kind: "study"; value: StudyName };

export type BarFrequency = "1m" | "5m" | "15m" | "1h" | "6h" | "1d" | "1w";

export type Instrument = {
  base: CommodityId | null;
  template: "new_crop" | "continuous" | null;
  option_month: string | null;
  data_series: DataSeries;
  bar_frequency: BarFrequency;
};

export type ResolvedInstrument = Omit<Instrument, "base"> & { base: CommodityId };

export type ResolvedWindow = {
  start_ts: number;
  end_ts: number;
  inclusive: { start: boolean; end: boolean };
};

export type SymbolicDateKind =
  | "now" | "today"
  | "this_week" | "last_week" | "next_week"
  | "this_month" | "last_month" | "next_month"
  | "this_quarter" | "this_year"
  | "mtd" | "ytd";

export type SymbolicDate = {
  year: number | null;
  month: number | null;
  day: number | null;
  time: string | null;
  kind: SymbolicDateKind | null;
};

export type SymbolicWindow = {
  start: SymbolicDate;
  end: SymbolicDate;
  inclusive: { start: boolean; end: boolean };
};

export type CommodityListSpec =
  | { kind: "list"; ids: CommodityId[] }
  | { kind: "group"; name: "main_4" };

export type IterateSpec =
  | { over: "none" }
  | { over: "commodities"; values: CommodityListSpec }
  | { over: "option_months"; values: { kind: "list"; ids: string[] } }
  | { over: "windows"; values: { kind: "list"; windows: SymbolicWindow[] } };

export type PriceAverageMethod =
  | "avg_price" | "net_change" | "net_change_pct" | "avg_bar_change";

export type SignalType = "buy" | "sell";

export type SignalFilter = {
  signal_type: SignalType;
  base: CommodityId | null;
  search_window: SymbolicWindow;
};

export type MetricWindowType = "bars" | "days";

export type MetricWindow = {
  count: number;
  window_type: MetricWindowType;
  inclusive: { start: boolean; end: boolean };
};

export type SignalMetric =
  | "price_delta" | "price_delta_pct" | "day_to_day_avg_change"
  | "max_price_gain" | "max_price_loss" | "avg_close";

export type SignalAggregate =
  | "average" | "median" | "sum" | "min" | "max" | "none";

export type SignalLookupSelect = "last" | "first" | "all" | "count";

export type Operation =
  | {
      kind: "price_average";
      iterate: IterateSpec;
      template: {
        instrument: Instrument;
        window: SymbolicWindow;
        methods: PriceAverageMethod[];
        primary_method: PriceAverageMethod;
      };
    }
  | {
      kind: "signal_window_metric";
      iterate: IterateSpec;
      template: {
        instrument: Instrument;
        signal_filter: SignalFilter;
        window: MetricWindow;
        metrics: SignalMetric[];
        primary_metric: SignalMetric;
        aggregate: SignalAggregate;
      };
    }
  | {
      kind: "signal_lookup";
      iterate: IterateSpec;
      template: {
        instrument: Instrument;
        signal_filter: SignalFilter;
        select: SignalLookupSelect;
        limit: number | null;
      };
    }
  | {
      kind: "price_lookup";
      iterate: IterateSpec;
      template: {
        instrument: Instrument;
        window: SymbolicWindow;
        select: "single" | "range";
      };
    };

export type Plan = {
  kind: "plan";
  interpretation: string;
  operations: Operation[];
};

export type UnsupportedReply = {
  kind: "unsupported";
  interpretation: string;
  reason: string;
};

export type ResolvedRun =
  | {
      kind: "price_average";
      instrument: ResolvedInstrument;
      window: ResolvedWindow;
      methods: PriceAverageMethod[];
      primary_method: PriceAverageMethod;
    }
  | {
      kind: "signal_window_metric";
      instrument: ResolvedInstrument;
      signal_filter: { signal_type: SignalType; base: CommodityId; search_window: ResolvedWindow };
      window: MetricWindow;
      metrics: SignalMetric[];
      primary_metric: SignalMetric;
      aggregate: SignalAggregate;
    }
  | {
      kind: "signal_lookup";
      instrument: ResolvedInstrument;
      signal_filter: { signal_type: SignalType; base: CommodityId; search_window: ResolvedWindow };
      select: SignalLookupSelect;
      limit: number | null;
    }
  | {
      kind: "price_lookup";
      instrument: ResolvedInstrument;
      window: ResolvedWindow;
      select: "single" | "range";
    };

export type ResolvedOperation = {
  operation_index: number;
  runs: ResolvedRun[];
};

export type ResolvedPlan = {
  interpretation: string;
  operations: ResolvedOperation[];
};

export type AssumptionLevel = "low" | "medium" | "high";

export type Assumption = {
  level: AssumptionLevel;
  code: string;
  message: string;
  field_path?: string;
};

export type Warning = {
  code: string;
  message: string;
  field_path?: string;
};

export type AssistantError = {
  code: string;
  message: string;
  field_path?: string;
  fatal: boolean;
};

export type PriceAverageRow = {
  commodity_id: CommodityId;
  instrument: ResolvedInstrument;
  window: ResolvedWindow;
  bar_count: number;
  values: Partial<Record<PriceAverageMethod, string>>;
  primary_method: PriceAverageMethod;
};

export type BarDisplay = "date_only" | "date_and_time";

export type SignalEventBreakdown = {
  signal_ts: number;
  signal_value: string;
  /** Requested calendar window: signal_ts → signal_ts + count days. */
  window: ResolvedWindow;
  first_bar_ts: number;
  first_bar_close: string;
  last_bar_ts: number;
  last_bar_close: string;
  metrics: Partial<Record<SignalMetric, string>>;
  bar_display: BarDisplay;
};

export type SignalWindowMetricRow = {
  commodity_id: CommodityId;
  instrument: ResolvedInstrument;
  signal_type: SignalType;
  search_window: ResolvedWindow;
  metric_window: MetricWindow;
  signal_count: number;
  aggregate: SignalAggregate;
  metrics: Partial<Record<SignalMetric, string>>;
  primary_metric: SignalMetric;
  per_signal_breakdown: SignalEventBreakdown[];
  bar_display: BarDisplay;
};

export type SignalLookupEvent = {
  signal_ts: number;
  signal_value: string;
  close: string;
};

export type SignalLookupRow = {
  commodity_id: CommodityId;
  instrument: ResolvedInstrument;
  signal_type: SignalType;
  search_window: ResolvedWindow;
  select: SignalLookupSelect;
  signal_count: number;
  events: SignalLookupEvent[];
  bar_display: BarDisplay;
};

export type PriceLookupBar = {
  timestamp: number;
  close: string;
};

export type PriceLookupRow = {
  commodity_id: CommodityId;
  instrument: ResolvedInstrument;
  window: ResolvedWindow;
  select: "single" | "range" | "last" | "first";
  bar_count: number;
  bars: PriceLookupBar[];
  bar_display: BarDisplay;
};

export type OperationResult =
  | { kind: "price_average"; rows: PriceAverageRow[] }
  | { kind: "signal_window_metric"; rows: SignalWindowMetricRow[] }
  | { kind: "signal_lookup"; rows: SignalLookupRow[] }
  | { kind: "price_lookup"; rows: PriceLookupRow[] };

export type AssistantResponse = {
  query: string;
  plan: Plan | null;
  unsupported: UnsupportedReply | null;
  resolved_plan: ResolvedPlan | null;
  assumptions: Assumption[];
  warnings: Warning[];
  errors: AssistantError[];
  results: OperationResult[];
};

export type AskAssistantRequest = {
  query: string;
  retries?: number;
  model?: string;
  temperature?: number;
  timezone?: string;
};

/**
 * POST /assistant/ask — proxied through NX to mono-bun @lhc/assistant.
 */
export function useAskAssistant() {
  return useMutation({
    mutationFn: async (request: AskAssistantRequest) => {
      const result = await fetchApiPost<AssistantResponse>(
        "assistant/ask",
        request,
      );
      return result;
    },
  });
}
